/**
 * useOnboardingStorage Hook
 * Handles localStorage persistence for onboarding state
 */

import { useState, useCallback, useEffect } from 'react';
import { local } from '@/utils/storage';
import { type OnboardingState, INITIAL_ONBOARDING_STATE } from '@/types/onboarding';

const STORAGE_KEY = 'onboarding';

/**
 * Type guard for OnboardingState
 */
function isOnboardingState(value: unknown): value is Partial<OnboardingState> {
  return value !== null && typeof value === 'object';
}

/**
 * Parse stored onboarding state from localStorage
 */
function getStoredState(): OnboardingState {
  const stored = local.get(STORAGE_KEY, isOnboardingState);
  if (!stored) {
    return INITIAL_ONBOARDING_STATE;
  }

  // Merge with initial state to handle missing fields from older versions
  return {
    ...INITIAL_ONBOARDING_STATE,
    ...stored,
    checklistProgress: {
      ...INITIAL_ONBOARDING_STATE.checklistProgress,
      ...stored.checklistProgress,
    },
  };
}

/**
 * Persist onboarding state to localStorage
 */
function persistState(state: OnboardingState): void {
  local.set(STORAGE_KEY, state);
}

export interface UseOnboardingStorageReturn {
  /** Current onboarding state */
  state: OnboardingState;
  /** Update state with partial changes */
  updateState: (updates: Partial<OnboardingState>) => void;
  /** Reset state to initial values */
  resetState: () => void;
}

/**
 * Hook for managing onboarding state with localStorage persistence
 *
 * @example
 * ```tsx
 * const { state, updateState, resetState } = useOnboardingStorage();
 *
 * // Mark welcome as shown
 * updateState({ welcomeShown: true });
 *
 * // Add completed tour
 * updateState({
 *   completedTours: [...state.completedTours, 'welcome']
 * });
 * ```
 */
export function useOnboardingStorage(): UseOnboardingStorageReturn {
  const [state, setState] = useState<OnboardingState>(getStoredState);

  // Sync state to localStorage whenever it changes
  useEffect(() => {
    persistState(state);
  }, [state]);

  const updateState = useCallback((updates: Partial<OnboardingState>) => {
    setState((prev) => {
      const newState: OnboardingState = {
        ...prev,
        ...updates,
        // Deep merge checklist progress if provided
        checklistProgress: updates.checklistProgress
          ? { ...prev.checklistProgress, ...updates.checklistProgress }
          : prev.checklistProgress,
        // Always update lastSeenAt
        lastSeenAt: new Date().toISOString(),
      };
      return newState;
    });
  }, []);

  const resetState = useCallback(() => {
    setState({
      ...INITIAL_ONBOARDING_STATE,
      lastSeenAt: new Date().toISOString(),
    });
  }, []);

  return { state, updateState, resetState };
}
